/**
 * AppointmentLinkSelect — paginated, lazy-loading "link an appointment" Select.
 *
 * Drop-in replacement for the two ad-hoc Select dropdowns previously inlined
 * in App.js (note-header link + create-note dialog link). Encapsulates:
 *   • Default 10-visible cap for snappy initial render.
 *   • "Load 5 more older appointments (N remaining)" affordance that does
 *     NOT close the Radix Select popover (uses onMouseDown + stopPropagation).
 *   • Lazy fetch of the FULL per-client appointment history on first open
 *     (the global `appointments` state in App.js only carries a ±1-day
 *     window for performance).
 *   • Always renders the currently-selected appointment even when it's
 *     outside the visible window.
 *
 * Caller responsibilities:
 *   • Provide `value` + `onChange(value, apt|null)`. The selected `apt` is
 *     passed back so the caller can run its own side effects (date-of-service
 *     sync, telehealth consent prefill, etc.).
 *   • Provide `renderLabel(apt) => string` to customize the visible text on
 *     each option. Defaults to `"<date> <time> - <client>"`.
 *
 * Common props:
 *   • clientIds?: string[]           — filter appointments to these clients.
 *     OPTIONAL. When omitted/empty, `appointments` is treated as a
 *     pre-filtered list (no internal client filter, no lazy fetch). Use
 *     this for group-scoped lists that are already filtered by the caller.
 *   • groupAware?: boolean (default false) — also include appointments whose
 *     selected_group_id/group_id resolves to a clientGroup containing any of
 *     `clientIds` as a member.
 *   • clientGroups?: ClientGroup[]   — for groupAware=true.
 *   • fallbackAppointment?: Appointment — render this in the dropdown so the
 *     selected value never appears blank if it's missing from both sources.
 *   • appointments: Appointment[]    — global ±1-day window OR a
 *     pre-filtered list when `clientIds` is omitted.
 *   • API: string                    — base API URL (used for lazy fetch).
 *   • triggerClassName?: string
 *   • noneLabel?: string             — text for the "no appointment" option.
 *   • placeholder?: string
 *   • testIdPrefix?: string          — prefix for option/load-more test IDs.
 *
 * NOT included by design:
 *   • Multi-client lazy fetch. We only fetch when exactly 1 clientId is
 *     supplied (the only path that's actually meaningful — the global
 *     window covers multi-client cases). For >1 client the component falls
 *     back to filtering the global `appointments` list.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';

const DEFAULT_PAGE_SIZE = 10;
const PAGE_INCREMENT = 5;

const defaultRenderLabel = (apt) => {
  const d = new Date(apt.start_time);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

export const AppointmentLinkSelect = ({
  value,
  onChange,
  clientIds,
  appointments = [],
  clientGroups = [],
  groupAware = false,
  fallbackAppointment = null,
  renderLabel = defaultRenderLabel,
  API,
  triggerClassName = '',
  noneLabel = 'No appointment',
  placeholder = 'Select appointment...',
  testIdPrefix = 'appt-link',
  disabled = false,
}) => {
  const [visibleCount, setVisibleCount] = useState(DEFAULT_PAGE_SIZE);
  const [fullList, setFullList] = useState(null); // null = not loaded; [] = loaded empty
  const [loading, setLoading] = useState(false);
  const fetchedClientIdRef = useRef(null);

  // Pre-filtered mode: caller passes a list already filtered by group / etc.
  // No internal client filter, no lazy fetch — just paginate the given list.
  const preFiltered = !clientIds || clientIds.length === 0;

  // Reset paging + clear lazy-fetched list when the target client changes —
  // a different client means the cached list is no longer relevant.
  const targetClientId = (!preFiltered && clientIds.length === 1) ? clientIds[0] : null;
  useEffect(() => {
    setVisibleCount(DEFAULT_PAGE_SIZE);
    if (fetchedClientIdRef.current !== targetClientId) {
      setFullList(null);
      fetchedClientIdRef.current = null;
    }
  }, [targetClientId]);

  const lazyFetch = useCallback(async () => {
    if (preFiltered) return; // caller-provided list — no fetch
    if (!targetClientId || loading) return;
    if (fetchedClientIdRef.current === targetClientId && fullList !== null) return;
    try {
      setLoading(true);
      const resp = await axios.get(`${API}/appointments`, {
        params: { client_id: targetClientId, summary: true },
      });
      setFullList(Array.isArray(resp.data) ? resp.data : []);
      fetchedClientIdRef.current = targetClientId;
    } catch (err) {
      console.warn('[AppointmentLinkSelect] history fetch failed:', err?.message);
      setFullList([]);
      fetchedClientIdRef.current = targetClientId;
    } finally {
      setLoading(false);
    }
  }, [API, targetClientId, loading, fullList, preFiltered]);

  const matchesClient = useCallback((apt) => {
    if (preFiltered) return true; // accept all — caller already filtered
    if (!clientIds || clientIds.length === 0) return false;
    if (apt.client_id && clientIds.includes(apt.client_id)) return true;
    if (groupAware && (apt.selected_group_id || apt.group_id)) {
      const gid = apt.selected_group_id || apt.group_id;
      const group = clientGroups.find(g => g.id === gid);
      if (group?.member_ids && clientIds.some(cid => group.member_ids.includes(cid))) {
        return true;
      }
    }
    return false;
  }, [clientIds, groupAware, clientGroups, preFiltered]);

  // Source preference: lazy-fetched full per-client history (when populated)
  // beats the global ±1-day window.
  const sourceList = (fullList && fullList.length > 0) ? fullList : appointments;
  const sortedAll = sourceList
    .filter(matchesClient)
    .sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
  const totalCount = sortedAll.length;
  const visible = sortedAll.slice(0, visibleCount);

  // Always include the currently-selected appointment so the trigger never
  // shows a blank label when the value isn't in the visible window.
  if (value && value !== 'none' && !visible.some(a => a.id === value)) {
    const fromAny = sourceList.find(a => a.id === value)
                  || appointments.find(a => a.id === value)
                  || fallbackAppointment;
    if (fromAny) visible.unshift(fromAny);
  }

  const remaining = Math.max(0, totalCount - visibleCount);

  const handleValueChange = (v) => {
    const newId = v === 'none' ? null : v;
    const apt = newId
      ? (sourceList.find(a => a.id === newId)
         || appointments.find(a => a.id === newId)
         || fallbackAppointment)
      : null;
    onChange(newId, apt);
  };

  const loadMore = (e) => {
    // onMouseDown + stopPropagation keeps Radix Select popover open.
    e.preventDefault();
    e.stopPropagation();
    setVisibleCount(c => c + PAGE_INCREMENT);
  };

  return (
    <Select
      value={value || ''}
      onOpenChange={(open) => { if (open) lazyFetch(); }}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger className={triggerClassName} data-testid={`${testIdPrefix}-trigger`}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none" data-testid={`${testIdPrefix}-option-none`}>{noneLabel}</SelectItem>
        {loading && fullList === null && (
          <div className="px-2 py-1.5 text-xs text-center text-gray-500 italic">
            Loading appointment history…
          </div>
        )}
        {visible.map(apt => (
          <SelectItem key={apt.id} value={apt.id} data-testid={`${testIdPrefix}-option-${apt.id}`}>
            {renderLabel(apt)}
          </SelectItem>
        ))}
        {remaining > 0 && (
          <div
            role="button"
            tabIndex={0}
            data-testid={`${testIdPrefix}-load-more-btn`}
            onMouseDown={loadMore}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') loadMore(e);
            }}
            className="cursor-pointer select-none px-2 py-1.5 mt-1 mx-1 text-xs text-center rounded-sm border border-dashed border-teal-300 text-teal-700 bg-teal-50 hover:bg-teal-100 focus:outline-none focus:ring-1 focus:ring-teal-400"
          >
            Load 5 more older appointments ({remaining} remaining)
          </div>
        )}
        {remaining === 0 && totalCount > DEFAULT_PAGE_SIZE && (
          <div className="px-2 py-1 text-[11px] text-center text-gray-400 italic">
            All {totalCount} appointments shown
          </div>
        )}
      </SelectContent>
    </Select>
  );
};

export default AppointmentLinkSelect;
